/**
 * 生成サムネイルの配色。
 *
 * 三原色は UI パレット（`--soremo-*`）とは別設計で、純度 100% の CMY を基準値
 * として持つ。UI 色を混ぜると彩度が落ちて発光表現が鈍るため流用しない。
 * ただし純度そのままではガラス越しに蛍光的に浮くので、描画時は OKLCH に変換して
 * chroma と明度を減衰させる。見え方の調整は RENDER_TUNING 側だけで完結させ、
 * 基準となる三原色の hex は動かさないこと。
 */

import { AudioCategory, MeasuredCategory } from '../../../audio-preview/core/audioCategory';

/** RGB 各成分 0-255 */
export type Rgb = readonly [number, number, number];

export interface AudioThumbnailPalette {
  /** カテゴリ由来の基本色 */
  base: Rgb;
  /** シード由来のアナログ色。並んだサムネイルを識別可能にする */
  accent: Rgb;
  /** accent の反対側に振った 3 色目。虹彩ガラスのような色相の飛びを作る */
  iris: Rgb;
  /** 色の雲の芯を作る、base より一段濃い色 */
  deep: Rgb;
  /** ソフトグロー用（base より明るく彩度が高い） */
  glow: Rgb;
  /** ガラス板の地色。ほぼ白 */
  surface: Rgb;
  /** 縁のダイクロイック（薄膜干渉）を表す 2 色 */
  edgeFrom: Rgb;
  edgeTo: Rgb;
}

/** カテゴリ別の基準色。UI トークンとは独立した三原色 */
const AUDIO_PRIMARY_HEX: Record<MeasuredCategory, string> = {
  music: '#00ffff',
  sound_effect: '#ffff00',
  voice: '#ff00ff',
};

/**
 * 見え方の調整はここだけで行う。
 * chroma を落としすぎると三原色の識別性（Cyan / Yellow / Magenta の区別）が
 * 失われて全部くすんだ中間色に見えるため、彩度より明度で落ち着かせること。
 */
const RENDER_TUNING = {
  /**
   * 彩度を揃える目標値（OKLCH の C）。
   * 純度 100% の CMY は OKLCH の chroma が大きく異なり（Cyan 0.13 / Yellow 0.21 /
   * Magenta 0.32）、一律スケールだと Magenta だけネオンのように浮く。
   */
  targetChroma: 0.16,
  /** 目標彩度への寄せ具合。1 で完全に均一、0 で原色そのまま */
  chromaBlend: 0.78,
  /** 明度を揃える目標値（OKLCH の L）。三原色間の明るさ差を均す */
  targetLightness: 0.85,
  /** 目標明度への寄せ具合。1 で完全に均一、0 で原色そのまま */
  lightnessBlend: 0.6,
  /**
   * 基本色の色相をシードで散らす幅（度）。
   * 最も濃い芯がこの色相になるので、ここが個体差の効き所。
   * 狭いと同カテゴリの一覧が全部同じ色に見える（Cyan なら Green 寄りから
   * Blue 寄りまで振れて、なお「寒色 = MUSIC」と読める幅にしてある）
   */
  baseHueJitter: 38,
  /** 基本色の彩度・明度をシードで散らす幅 */
  baseChromaJitter: 0.04,
  baseLightnessJitter: 0.035,
  /**
   * 色相の広がり具合をシードで決める範囲。1 に近いほど虹彩ガラスのように
   * 色が飛び、0 に近いほど単色に近づく。この差が個体差の主な源になる
   */
  hueSpreadRange: [0.32, 1] as const,
  /**
   * 副色の色相オフセット範囲（度）。hueSpread で縮む。
   * iris は accent からさらに同じ向きへ進む量で、3 色が基準色相から片側へ並ぶ
   * 一方向のランプになる。両側へ振ると補色同士になり、重なりの乗算で
   * 灰色に濁る（薄膜干渉も実際には一方向の色相スイープに見える）。
   */
  accentHueOffsetRange: [26, 56] as const,
  irisHueOffsetRange: [20, 44] as const,
  accentChromaScale: 0.95,
  accentLightnessDelta: -0.05,
  irisChromaScale: 0.85,
  irisLightnessDelta: 0.02,
  /**
   * 雲の芯になる色。明度で沈めるより彩度で濃さを出す。
   * 絶対値の明度を指定すると Yellow だけオリーブに濁るため、base からの
   * 相対差で表現して三原色それぞれの自然な明るさを保つ。
   */
  deepLightnessDelta: -0.09,
  deepChromaScale: 1.55,
  /** グロー色の明度と chroma */
  glowLightness: 0.95,
  glowChromaScale: 0.8,
  /** 縁のダイクロイック色。板の切り口が光を分けている見え方を狙って彩度を上げる */
  edgeLightness: 0.72,
  edgeChromaScale: 1.85,
  /**
   * ガラス板そのものの色。ほぼ白だが、カテゴリの色相をごく僅かに含ませることで
   * 無彩色の白より「色が透けている板」に見える
   */
  surfaceLightness: 0.985,
  surfaceChroma: 0.008,
  /**
   * neutral（カテゴリ不明）。彩度は落とすが、下げすぎると白地の上で
   * 灰色の濁りに見えるため、淡いパステルに留めて「未分類」を示す
   */
  neutralChroma: 0.06,
  neutralLightness: 0.86,
};

// =============================================================================
// シード
// =============================================================================

/**
 * 配色と配置の種を音源 URL から決める。
 *
 * 同じ音源なら一覧のカードとフローティングバーで同じ絵にしたいので、両者が
 * 確実に共有している値だけを使う。署名付き URL のクエリは毎回変わるので落とす。
 */
export const audioThumbnailSeed = (src: string): string => src.split('?')[0];

/** FNV-1a 32bit ハッシュ */
export function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** シードから決定論的な擬似乱数列を作る（mulberry32） */
export function createRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// =============================================================================
// 色空間変換（sRGB <-> OKLCH）
// =============================================================================

interface Oklch {
  /** 知覚的明度 0-1 */
  l: number;
  /** 彩度 */
  c: number;
  /** 色相（度） */
  h: number;
}

const srgbToLinear = (v: number): number =>
  v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);

const linearToSrgb = (v: number): number =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

function hexToRgb(hex: string): Rgb {
  const value = parseInt(hex.replace('#', ''), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function rgbToOklch([r, g, b]: Rgb): Oklch {
  const lr = srgbToLinear(r / 255);
  const lg = srgbToLinear(g / 255);
  const lb = srgbToLinear(b / 255);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const hue = (Math.atan2(okB, okA) * 180) / Math.PI;
  return { l: okL, c: Math.hypot(okA, okB), h: hue < 0 ? hue + 360 : hue };
}

function oklchToLinearSrgb({ l, c, h }: Oklch): [number, number, number] {
  const rad = (h * Math.PI) / 180;
  const okA = c * Math.cos(rad);
  const okB = c * Math.sin(rad);

  const l_ = (l + 0.3963377774 * okA + 0.2158037573 * okB) ** 3;
  const m_ = (l - 0.1055613458 * okA - 0.0638541728 * okB) ** 3;
  const s_ = (l - 0.0894841775 * okA - 1.291485548 * okB) ** 3;

  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

/** 丸め誤差ぶんの逸脱は色域内とみなす */
const GAMUT_EPSILON = 0.0015;

const isInSrgbGamut = (color: Oklch): boolean =>
  oklchToLinearSrgb(color).every(v => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON);

/**
 * sRGB の色域に収まるまで chroma を落とす。
 *
 * 色域外の OKLCH をそのまま RGB へ変換して各チャンネルをクリップすると
 * 色相がずれる。特に Yellow は色域が狭く、少し彩度を上げただけで
 * オリーブや黄緑に転ぶ。ずれを彩度側で吸収して色相と明度を守る。
 */
function fitToSrgbGamut(color: Oklch): Oklch {
  if (isInSrgbGamut(color)) return color;

  let inGamut = 0;
  let outOfGamut = color.c;
  for (let i = 0; i < 14; i += 1) {
    const mid = (inGamut + outOfGamut) / 2;
    if (isInSrgbGamut({ ...color, c: mid })) inGamut = mid;
    else outOfGamut = mid;
  }
  return { ...color, c: inGamut };
}

function oklchToRgb(color: Oklch): Rgb {
  const [lr, lg, lb] = oklchToLinearSrgb(fitToSrgbGamut(color));
  return [
    Math.round(clamp01(linearToSrgb(lr)) * 255),
    Math.round(clamp01(linearToSrgb(lg)) * 255),
    Math.round(clamp01(linearToSrgb(lb)) * 255),
  ];
}

// =============================================================================
// パレット生成
// =============================================================================

/** -1 から 1 の一様乱数 */
const bipolar = (random: () => number): number => random() * 2 - 1;

/** カテゴリとシードから基準の OKLCH を決める */
function resolveBaseOklch(category: AudioCategory, random: () => number): Oklch {
  const { baseHueJitter, baseChromaJitter, baseLightnessJitter } = RENDER_TUNING;

  if (category === 'neutral') {
    // カテゴリ不明。色相だけシードで散らし、彩度を落として未分類であることを示す
    return {
      l: RENDER_TUNING.neutralLightness,
      c: RENDER_TUNING.neutralChroma,
      h: random() * 360,
    };
  }

  const primary = rgbToOklch(hexToRgb(AUDIO_PRIMARY_HEX[category]));
  const { targetLightness, lightnessBlend, targetChroma, chromaBlend } = RENDER_TUNING;
  const lightness = primary.l + (targetLightness - primary.l) * lightnessBlend;
  const chroma = primary.c + (targetChroma - primary.c) * chromaBlend;

  // 同カテゴリでもまったく同じ色にならないよう、基準色そのものを僅かに散らす
  return {
    l: clamp01(lightness + bipolar(random) * baseLightnessJitter),
    c: Math.max(0.02, chroma + bipolar(random) * baseChromaJitter),
    h: (primary.h + bipolar(random) * baseHueJitter + 360) % 360,
  };
}

/**
 * カテゴリとシード文字列からサムネイル用パレットを作る。
 *
 * 個体差は 3 段構えで作る。基準色そのものの僅かなゆらぎ、色相の広がり具合
 * （単色寄りか虹彩ガラス寄りか）、そして副色 2 色の振れ方向と量。
 * 同カテゴリが並んでも同じ絵にならず、かつ最も濃い芯が基準色相を保つので
 * カテゴリとしての identity（Cyan = MUSIC 等）は残る。
 */
export function createAudioThumbnailPalette(
  category: AudioCategory,
  seed: string,
): AudioThumbnailPalette {
  const random = createRandom(hashSeed(seed));
  const base = resolveBaseOklch(category, random);

  const [minSpread, maxSpread] = RENDER_TUNING.hueSpreadRange;
  const spread = minSpread + random() * (maxSpread - minSpread);
  // ランプの向き。同じカテゴリでも Green 寄りへ流れる個体と Blue 寄りへ
  // 流れる個体が生まれる
  const direction = random() < 0.5 ? -1 : 1;

  const pick = ([min, max]: readonly [number, number]): number =>
    (min + random() * (max - min)) * spread;

  const accentOffset = pick(RENDER_TUNING.accentHueOffsetRange);
  const irisOffset = accentOffset + pick(RENDER_TUNING.irisHueOffsetRange);
  const hueAt = (offset: number): number => (base.h + direction * offset + 360) % 360;

  const accent: Oklch = {
    l: clamp01(base.l + RENDER_TUNING.accentLightnessDelta),
    c: base.c * RENDER_TUNING.accentChromaScale,
    h: hueAt(accentOffset),
  };
  const iris: Oklch = {
    l: clamp01(base.l + RENDER_TUNING.irisLightnessDelta),
    c: base.c * RENDER_TUNING.irisChromaScale,
    h: hueAt(irisOffset),
  };

  const edge = (hue: number): Rgb =>
    oklchToRgb({
      l: RENDER_TUNING.edgeLightness,
      c: base.c * RENDER_TUNING.edgeChromaScale,
      h: hue,
    });

  return {
    base: oklchToRgb(base),
    accent: oklchToRgb(accent),
    iris: oklchToRgb(iris),
    deep: oklchToRgb({
      l: clamp01(base.l + RENDER_TUNING.deepLightnessDelta),
      c: base.c * RENDER_TUNING.deepChromaScale,
      h: base.h,
    }),
    glow: oklchToRgb({
      l: RENDER_TUNING.glowLightness,
      c: base.c * RENDER_TUNING.glowChromaScale,
      h: base.h,
    }),
    surface: oklchToRgb({
      l: RENDER_TUNING.surfaceLightness,
      c: RENDER_TUNING.surfaceChroma,
      h: base.h,
    }),
    // 縁もランプの両端を使い、切り口で色が分かれている見え方にする
    edgeFrom: edge(base.h),
    edgeTo: edge(iris.h),
  };
}

/** CSS の rgba() 文字列に変換する */
export const rgba = ([r, g, b]: Rgb, alpha: number): string =>
  `rgba(${r}, ${g}, ${b}, ${alpha})`;
